// lh_read-more.js - Vanilla JavaScript Read More Component for Shopify

class ReadMore {
  constructor(selector, options = {}) {
    this.options = {
      maxCharacters: null,
      maxHeight: null,
      readMoreText: "Read more",
      readLessText: "Close",
      fadeHeight: 32, // Height of fade gradient
      ...options,
    };

    this.elements = document.querySelectorAll(selector);
    this.init();
  }

  init() {
    this.elements.forEach((element) => {
      this.setupReadMore(element);
    });
  }

  setupReadMore(element) {
    element.style.display = "block";
    const originalContent = element.innerHTML;
    const textContent = element.textContent || element.innerText;

    // Get options from data attributes or use defaults
    const maxCharacters =
      parseInt(element.getAttribute("maxcharacters")) ||
      this.options.maxCharacters;
    const maxHeight =
      parseInt(element.getAttribute("maxheight")) || this.options.maxHeight;
    const readMoreText =
      element.getAttribute("readmoretext") || this.options.readMoreText;
    const readLessText =
      element.getAttribute("readlesstext") || this.options.readLessText;

    let shouldTruncate = false;
    let truncatedContent = originalContent;

    // Check if truncation is needed
    if (maxCharacters && textContent.length > maxCharacters) {
      shouldTruncate = true;
      // Find the position to cut at maxCharacters within the HTML
      truncatedContent = this.truncateHTML(originalContent, maxCharacters);
    } else if (maxHeight) {
      // Temporarily set max height to check if content overflows
      const originalHeight = element.style.height;
      const originalOverflow = element.style.overflow;

      element.style.height = "auto";
      element.style.overflow = "visible";

      if (element.scrollHeight > maxHeight) {
        shouldTruncate = true;
      }

      element.style.height = originalHeight;
      element.style.overflow = originalOverflow;
    }

    if (!shouldTruncate) {
      return; // No truncation needed
    }

    // Create wrapper structure
    const wrapper = document.createElement("div");
    wrapper.className = "read-more-wrapper";

    const contentDiv = document.createElement("div");
    contentDiv.className = "read-more-content";

    const button = document.createElement("button");
    button.className = "read-more-button";
    button.textContent = readMoreText;
    button.style.cssText = `
      background: none;
      border: none;
      color: #425b34;
      text-decoration: underline;
      cursor: pointer;
      font-size: 0.875rem;
      font-weight: 500;
      margin-top: 0.5rem;
      padding: 0;
      transition: color 0.2s ease;
    `;

    // Add hover effect
    button.addEventListener("mouseenter", () => {
      button.style.color = "#425b34";
    });
    button.addEventListener("mouseleave", () => {
      button.style.color = "#425b34";
    });

    let isExpanded = false;
    let savedScrollPosition = null;

    const updateContent = () => {
      if (maxCharacters) {
        contentDiv.innerHTML = isExpanded ? originalContent : truncatedContent;
      } else {
        contentDiv.innerHTML = originalContent;
        if (maxHeight) {
          contentDiv.style.maxHeight = isExpanded ? "none" : `${maxHeight}px`;
          contentDiv.style.overflow = isExpanded ? "visible" : "hidden";
          contentDiv.style.position = "relative";

          // Add/remove fade gradient
          let fadeDiv = contentDiv.querySelector(".read-more-fade");
          if (!isExpanded && !fadeDiv) {
            fadeDiv = document.createElement("div");
            fadeDiv.className = "read-more-fade";
            fadeDiv.style.cssText = `
              position: absolute;
              bottom: 0;
              left: 0;
              right: 0;
              height: ${this.options.fadeHeight}px;
              background: linear-gradient(to top, #F2ECE3, transparent);
              pointer-events: none;
            `;
            contentDiv.appendChild(fadeDiv);
          } else if (isExpanded && fadeDiv) {
            fadeDiv.remove();
          }
        }
      }

      button.textContent = isExpanded ? readLessText : readMoreText;
    };

    button.addEventListener("click", () => {
      const wasExpanded = isExpanded;

      // Save scroll position when opening
      if (!isExpanded && !wasExpanded) {
        savedScrollPosition = window.pageYOffset;
      }

      isExpanded = !isExpanded;
      updateContent();

      // Restore saved scroll position when closing
      if (wasExpanded && !isExpanded && savedScrollPosition !== null) {
        // Small delay to ensure content has updated
        setTimeout(() => {
          window.scrollTo({
            top: savedScrollPosition,
            behavior: "smooth",
          });
          savedScrollPosition = null; // Reset after use
        }, 50);
      }
    });

    // Replace original element content
    element.innerHTML = "";
    wrapper.appendChild(contentDiv);
    wrapper.appendChild(button);
    element.appendChild(wrapper);

    // Initial setup
    updateContent();
  }

  truncateHTML(html, maxChars) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    let charCount = 0;

    const walkNodes = (node) => {
      if (charCount >= maxChars) return false;

      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (charCount + text.length > maxChars) {
          node.textContent = text.substring(0, maxChars - charCount) + "...";
          charCount = maxChars;
          return false;
        }
        charCount += text.length;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const children = Array.from(node.childNodes);
        for (let child of children) {
          if (!walkNodes(child)) {
            // Remove remaining siblings
            let nextSibling = child.nextSibling;
            while (nextSibling) {
              const toRemove = nextSibling;
              nextSibling = nextSibling.nextSibling;
              toRemove.remove();
            }
            break;
          }
        }
      }
      return true;
    };

    walkNodes(tempDiv);
    return tempDiv.innerHTML;
  }
}

// Auto-initialize when DOM is ready
function initReadMore() {
  new ReadMore("readmore");
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initReadMore);
} else {
  initReadMore();
}
document.addEventListener("shopify:section:load", initReadMore);
window.ReadMore = ReadMore;
